import { isMobileAppleDevice } from '@shared/utils/detectAppleDevice';

export const getUiConfig = (options) => {
	const {
		features: {
			isQualitySettingsEnabled,
			isPlaybackSpeedEnabled,
			isSharingEnabled,
			isLogoEnabled,
			isContentInfoEnabled,
			forceMinimalTimeline,
			isLiveBadgeEnabled,
			isStopEnabled,
			isPauseEnabled,
			isPlayerTimeEnabled,
			isTimelineEnabled,
			isLoopBackgroundMode,
			nativeFullscreenOnIos,
		} = {},
		device: {
			airPlayAvailable,
			isChromecastAvailable,
			isPictureInPictureAvailable,
			isFullscreen,
			isTouchDevice, // pointer: coarse -> smartphone, tablet, maybe MS Surface
			isMobileViewport, // viewport: mobile -> smartphone, tablet / matchMedia((max-width: 1023px))
			isMobileDevice, // window width below 1024px && touch (pointer: coarse)
			isLandscape, // window width > window height, any device
		} = {},
		appState: {
			isInCouchMode,
			isCinemaMode,
			isPreviewMode,
			isMobileWebApp, // Touch, window width below 1024px, web app (excluding embedded)
			googleImaIsActive,
		} = {},
		playbackState: {
			isLive,
			isAutoplayMuted,
			playerIsAdModeIdle,
			playerIsAdModePaused,
			isAdmode,
			isPlayingOrPaused,
		} = {},
		environment: {
			isEmbedded,
			isFullPageMode,
			isViewMode,
			isTaggingToolMode,
			isManager,
		} = {},
		event: {
			customTextEnabled,
			customOverlayImageEnabled,
			streamURL,
			hasTimelineItems,
			productPlacementEnabled,
			showScoreboardScore,
			showScoreboardTime,
			previewLimitReached,
			clientLogoTargetUrl,
		} = {},
	} = options;

	const usesFakeFullscreenOnIos = isMobileAppleDevice() && isFullPageMode && !nativeFullscreenOnIos;
	const isIosFakeFullscreenLandscape = usesFakeFullscreenOnIos && isLandscape;

	return {
		overlays: {
			enabled:            Boolean(!googleImaIsActive || playerIsAdModeIdle || playerIsAdModePaused),
			customOverlayImage: {
				enabled: Boolean(customOverlayImageEnabled),
			},
			gameInformation: {
				enabled:    Boolean((showScoreboardScore || showScoreboardTime || customTextEnabled) && !isAdmode && !isPreviewMode),
				scoreBoard: { enabled: Boolean(showScoreboardScore) },
				gameClock:  { enabled: Boolean(showScoreboardTime) },
				customText: { enabled: Boolean(customTextEnabled) },
			},
			videoInteractionFeedback: {
				enabled:   Boolean(!isPreviewMode && !isLoopBackgroundMode),
				touchMode: Boolean(isTouchDevice), // todo controls1
				playPause: {
					enabled: Boolean(!isTouchDevice),
				},
			},
			logo: {
				enabled: Boolean(!isManager && !isLoopBackgroundMode),
			},
			bannerAd: {
				enabled: Boolean(!isManager && (!isMobileWebApp || (isMobileWebApp && isLandscape)) && !isPreviewMode && !isLoopBackgroundMode),
				visible: true,
			},
			skipAd: {
				enabled: Boolean(isAdmode && !playerIsAdModeIdle),
			},
			playerContentInfo: {
				enabled: Boolean(isContentInfoEnabled && isEmbedded && !isAdmode && !isFullscreen && !isTaggingToolMode && !isPreviewMode && !isLoopBackgroundMode),
			},
			tagTimeline: {
				enabled: Boolean(isTimelineEnabled && !isAdmode && !isInCouchMode && !isMobileWebApp && !isPreviewMode && hasTimelineItems &&
					(isEmbedded ? (!forceMinimalTimeline && !isLoopBackgroundMode) : (isCinemaMode || isFullscreen))),
			},
			playerControls: {
				enabled:           Boolean(!isPreviewMode && !isLoopBackgroundMode),
				position:          isTaggingToolMode ? 'below' : 'over',
				controlsFullWidth: Boolean(isTaggingToolMode),
				showSettingsAtTop: Boolean(isMobileWebApp && !isTaggingToolMode),
				// minimal timeline
				timeline:          {
					enabled: Boolean(isTimelineEnabled &&
						(
							isInCouchMode ||
							isAdmode ||
							(isEmbedded && forceMinimalTimeline) ||
							isMobileWebApp ||
							(isFullPageMode && !hasTimelineItems)
						)),
					placement:  (isInCouchMode || (isMobileWebApp && !isFullscreen && !isLandscape)) ? 'bottom' : 'top',
					fullWidth:  Boolean(isInCouchMode || isMobileWebApp),
					isSeekable: Boolean(!isAdmode && !isInCouchMode),
					scrubber:   {
						enabled: Boolean(!isAdmode && !isInCouchMode),
					},
				},
				playToggle: {
					enabled: Boolean(!isTouchDevice),
					stop:    {
						enabled: Boolean(isStopEnabled),
					},
					pause: {
						enabled: Boolean(isPauseEnabled),
					},
				},
				reload: { // only used in cockpit
					enabled: false,
				},
				volumeControl: {
					enabled:    true,
					muteToggle: {
						enabled: true,
					},
					volumeSlider: {
						enabled: Boolean(!isTouchDevice),
					},
				},
				replay: {
					enabled: Boolean(isInCouchMode), // Couchmode clip replay
				},
				timeDisplay: {
					enabled:     Boolean(isPlayerTimeEnabled && !isInCouchMode),
					currentTime: {
						enabled: true,
					},
					duration: {
						enabled: true,
					},
				},
				badgeLive: {
					enabled: Boolean(isLiveBadgeEnabled && isLive && !isAdmode),
				},
				tagNavigation: {
					enabled: Boolean(isInCouchMode),
				},
				likeButton: {
					enabled: Boolean(isInCouchMode),
				},
				qualityString: { // only used in cockpit
					enabled: false,
				},
				clientLogo: {
					enabled:   Boolean(isEmbedded && isLogoEnabled && !isAdmode && !isInCouchMode),
					targetURL: clientLogoTargetUrl,
				},
				shareButton: {
					enabled: Boolean(isSharingEnabled && !isAdmode && (isInCouchMode || isEmbedded)),
				},
				audioSettings: {
					enabled: Boolean(!isAdmode),
				},
				playbackSpeed: {
					enabled: Boolean(isPlaybackSpeedEnabled && !isAdmode),
				},
				qualitySettings: {
					enabled: Boolean(isQualitySettingsEnabled && !isAdmode),
				},
				cinemaMode: {
					enabled: Boolean(!isAdmode && !isInCouchMode && isFullPageMode && !isFullscreen && !isMobileViewport),
				},
				airPlay: {
					enabled: Boolean(airPlayAvailable && !isAdmode && !isInCouchMode),
				},
				chromecast: {
					enabled: Boolean(isChromecastAvailable && !!streamURL && !isAdmode && !isInCouchMode),
				},
				fullscreen: {
					enabled: Boolean(!isAdmode && !isTaggingToolMode && !isIosFakeFullscreenLandscape),
				},
				pictureInPicture: {
					enabled: Boolean(!isAdmode && !isInCouchMode && isFullPageMode && isPictureInPictureAvailable && isPlayingOrPaused),
				},
				toggleSidebar: {
					enabled: false,
				},
			},
			playToggleOverlay: {
				enabled: Boolean(isTouchDevice && !isPreviewMode && !isLoopBackgroundMode),
			},
			gameEventOverlay: {
				enabled: Boolean(isInCouchMode),
			},
			couchModeClose: {
				enabled: Boolean(isInCouchMode),
			},
			backButton: {
				enabled: Boolean(isMobileWebApp),
			},
			clipsPreview: {
				enabled: Boolean(!isMobileAppleDevice() && (isViewMode || isFullPageMode) && isLandscape && isMobileDevice && !isAdmode && !isInCouchMode && !isPreviewMode),
			},
			productPlacementInfo: {
				enabled: Boolean(productPlacementEnabled && !isTaggingToolMode && !isAdmode && !isManager && !isPreviewMode && !isLoopBackgroundMode),
			},
			interactionOverlay: {
				enabled: Boolean(!isPreviewMode && !isLoopBackgroundMode),
			},
			paywallPreview: {
				enabled: Boolean(isPreviewMode),
			},
			autoplayMutedIndicator: {
				enabled:        Boolean(isAutoplayMuted && !isLoopBackgroundMode && (!isPreviewMode || !previewLimitReached)),
				disableDismiss: Boolean(isPreviewMode),
			},
			liveBorder: {
				enabled: Boolean(isTaggingToolMode && isLive && !isMobileWebApp),
			},
			iosRotateInfo: {
				enabled: Boolean(usesFakeFullscreenOnIos && !isLandscape && !isAdmode && !isPreviewMode && !isLoopBackgroundMode),
			},
		},
	};
};

const getters = {
	// Dynamic UI configuration based on state and other modules
	uiConfig: (_state, _getters, rootState, rootGetters) => {
		return getUiConfig({
			features: {
				isQualitySettingsEnabled: rootGetters['features/isQualitySettingsEnabled'],
				isPlaybackSpeedEnabled:   rootGetters['features/isPlaybackSpeedEnabled'],
				isSharingEnabled:         rootGetters['features/isSharingEnabled'],
				isLogoEnabled:            rootGetters['features/isLogoEnabled'],
				isContentInfoEnabled:     rootGetters['features/isPlayerContentInfoEnabled'],
				forceMinimalTimeline:     rootGetters['features/forceMinimalTimeline'],
				isLiveBadgeEnabled:       rootGetters['features/isLiveBadgeEnabled'],
				isStopEnabled:            rootGetters['features/isStopEnabled'],
				isPauseEnabled:           rootGetters['features/isPauseEnabled'],
				isPlayerTimeEnabled:      rootGetters['features/isPlayerTimeEnabled'],
				isTimelineEnabled:        rootGetters['features/isTimelineEnabled'],
				isLoopBackgroundMode:     rootGetters['features/isLoopBackgroundMode'],
				nativeFullscreenOnIos:    rootGetters['features/nativeFullscreenOnIos'],
			},
			device: {
				airPlayAvailable:            rootGetters['player/isAirPlayAvailable'],
				isChromecastAvailable:       rootGetters['player/isChromecastAvailable'],
				isPictureInPictureAvailable: rootGetters['player/isPictureInPictureAvailable'],
				isFullscreen:                rootGetters['device/fullscreen'],
				isMobileDevice:              rootGetters['device/isMobile'],
				isMobileViewport:            rootGetters['device/isMobileViewport'],
				isNarrowTaggingTool:         rootGetters['device/isNarrowTaggingTool'],
				isTouchDevice:               rootGetters['device/touch'],
				isLandscape:                 rootGetters['device/isLandscape'],
			},
			appState: {
				isInCouchMode:     rootGetters['app/isInCouchMode'],
				isCinemaMode:      rootGetters['app/isCinemaMode'],
				isPreviewMode:     rootGetters['app/isPreviewMode'],
				isMobileWebApp:    rootGetters['app/isMobileWebApp'],
				googleImaIsActive: rootGetters['player/googleImaIsActive'],
			},
			playbackState: {
				isLive:               rootGetters['event/isLive'],
				isPlayingOrPaused:    rootGetters['player/isPlayingOrPaused'],
				isAutoplayMuted:      rootGetters['player/isAutoplayMuted'],
				playerIsAdModeIdle:   rootGetters['player/isAdModeIdle'],
				playerIsAdModePaused: rootGetters['player/isAdModePaused'],
				isAdmode:             rootGetters['player/isAdMode'],
			},
			environment: {
				isEmbedded:        rootGetters['app/isEmbedded'],
				isFullPageMode:    rootGetters['app/isFullPageMode'],
				isViewMode:        rootGetters['app/isViewMode'],
				isTaggingToolMode: rootGetters['app/isTaggingToolMode'],
				isManager:         window.managerDomain && document.location.host === window.managerDomain,
			},
			event: {
				showScoreboardScore:       rootGetters['event/showScoreboardScore'],
				showScoreboardTime:        rootGetters['event/showScoreboardTime'],
				previewLimitReached:       rootGetters['event/previewLimitReached'],
				clientLogoTargetUrl:       rootGetters['event/eventURL'],
				hasTimelineItems:          rootGetters['event/hasTimelineItems'],
				customTextEnabled:         rootGetters['event/customTextEnabled'],
				customOverlayImageEnabled: rootGetters['event/customOverlayImageEnabled'],
				productPlacementEnabled:   rootGetters['event/productPlacementEnabled'],
				streamURL:                 rootGetters['event/streamURL'],
			},
		});
	},
};

export default {
	namespaced: true,
	getters,
};
